/**
 * "Viewing:" — the country → campaign scope selector above a rules list.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Two dropdowns, nested. The campaign list only appears once a country is
 * chosen and only offers campaigns in THAT country, because a campaign
 * threshold is meaningful relative to the country it runs in — and the pair is
 * the precedence chain the engine walks (campaign → country → default).
 */

import { type FC, useMemo } from 'react';
import { Globe } from 'lucide-react';

import { useCountries } from '@/hooks/smswiser/core/useSMSWiser';
import { useAllCampaigns } from '@/hooks';
import { countryToFlagEmoji } from '@/utils/country-flag-emoji';

import {
  isDefaultScope,
  normScope,
  parseCampaignToken,
  scopeCampaignKey,
  scopeTarget,
  type OverrideUsage,
  type TagRuleScope,
} from '../model';
import { usePinnedTagRuleScope } from '../scope-pin';
import { campaignScopeOptions } from './campaign-scope-options';
import { ScopeCombobox, type ScopeOption } from './combobox';
import { ScopeSummary } from './ScopeSummary';

export const TagRuleScopeBar: FC<{
  scope: TagRuleScope;
  onScopeChange: (next: TagRuleScope) => void;
  inUse: OverrideUsage;
}> = ({ scope, onScopeChange, inUse }) => {
  const pinned = usePinnedTagRuleScope();
  const { data: countries = [] } = useCountries();
  const { data: campaigns = [] } = useAllCampaigns();
  const countryKey = normScope(scope.countryKey ?? '');

  const countryOptions = useMemo<ScopeOption[]>(
    () =>
      [...countries]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((c) => {
          const key = normScope(c.key);
          return {
            value: key,
            label: c.name,
            icon: countryToFlagEmoji(c.name),
            count: inUse.countries.get(key) ?? 0,
            // The key doubles as the country's id in stored overrides.
            search: `${c.name} ${key}`.toLowerCase(),
          };
        }),
    [countries, inUse],
  );

  // Campaigns for the chosen country, matching ones first (see campaignScopeOptions).
  const campaignOptions = useMemo<ScopeOption[]>(
    () => campaignScopeOptions({ campaigns: campaigns ?? [], countries, countryKey, inUse }),
    [campaigns, countries, countryKey, inUse],
  );

  // Pinned (the Flow Optimizer drawer): the scope is not a choice — you opened
  // one campaign and its header already says which. A disabled picker would
  // just be furniture.
  if (pinned) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-border bg-muted/20 px-3 py-2">
      <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <Globe className="h-3.5 w-3.5" aria-hidden />
        Viewing
      </span>

      <ScopeCombobox
        label="Country"
        value={countryKey}
        placeholder="All countries (default)"
        options={countryOptions}
        // Changing country drops the campaign: the old one belongs elsewhere.
        onChange={(v) => onScopeChange(v ? { countryKey: v } : {})}
      />

      {countryKey ? (
        <ScopeCombobox
          label="Campaign"
          value={scopeCampaignKey(scope)}
          placeholder={
            campaignOptions.length ? 'All campaigns in this country' : 'No campaigns here'
          }
          options={campaignOptions}
          showProviderFilter
          wide
          disabled={campaignOptions.length === 0}
          onChange={(v) =>
            onScopeChange({ countryKey, ...(v ? parseCampaignToken(v) : {}) })
          }
        />
      ) : null}

      {!isDefaultScope(scope) && (
        <ScopeSummary
          target={scopeTarget(scope)}
          countLabel={
            scopeTarget(scope) === 'campaign'
              ? inUse.campaigns.get(scopeCampaignKey(scope)) ?? 0
              : inUse.countries.get(countryKey) ?? 0
          }
        />
      )}
    </div>
  );
};
